import React, { useState } from 'react';
import { Trans } from '@lingui/macro';
import { numberWithSpaces } from '../../../helpers/index';
import { getYearColor } from '../../../helpers/reportHelpers';

interface IBreakdownRow {
    label: string;
    date?: string;
    values: { year: number; value: number }[];
}

interface IProps {
    title: string;
    subtitle?: string;
    columns: { year: number; label: string }[];
    rows: IBreakdownRow[];
}

type SortDir = 'asc' | 'desc';

/** Ordered Slovak month names (index = ordinal 0–11) */
const MONTH_ORDER = [
    'január', 'február', 'marec', 'apríl', 'máj', 'jún',
    'júl', 'august', 'september', 'október', 'november', 'december'
];

/** Ordered Slovak weekday names Mon–Sun (index = ordinal 0–6) */
const WEEKDAY_ORDER = [
    'pondelok', 'utorok', 'streda', 'štvrtok', 'piatok', 'sobota', 'nedeľa'
];

/**
 * Returns a numeric ordinal for a label so months, weekdays, weeks, and
 * time slots sort in their natural order rather than alphabetically.
 * Falls back to the label string itself for plain name sorting.
 */
function getLabelOrdinal(label: string): number | string {
    const lower = label.trim().toLowerCase();

    // Month name: "január", "február", …
    const mIdx = MONTH_ORDER.indexOf(lower);
    if (mIdx !== -1) return mIdx;

    // Weekday name: "pondelok", "utorok", …
    const wIdx = WEEKDAY_ORDER.indexOf(lower);
    if (wIdx !== -1) return wIdx;

    // "Týždeň N" — extract N
    const weekMatch = lower.match(/^týždeň\s+(\d+)/);
    if (weekMatch) return parseInt(weekMatch[1], 10);

    // Time slot "HH:MM – HH:MM" — sort by start hour
    const timeMatch = label.match(/^(\d{1,2}):/);
    if (timeMatch) return parseInt(timeMatch[1], 10);

    return lower;
}

/**
 * Compute year-over-year percentage change.
 * Returns null if the previous year value is 0 or missing.
 */
function computeYoYPercent(currentValue: number, previousValue: number): number | null {
    if (previousValue === 0) return null;
    return ((currentValue / previousValue) - 1) * 100;
}

/**
 * Table showing year-over-year comparison across multiple rows
 * (e.g. by location, by zone, or daily breakdown within a week).
 * Includes tooltip popups on cells and percentage comparison columns.
 */
const ReportComparisonTable = ({ title, subtitle, columns, rows }: IProps) => {
    const [sortCol, setSortCol] = useState<'label' | number>('label');
    const [sortDir, setSortDir] = useState<SortDir>('asc');

    if (!rows || rows.length === 0 || !columns || columns.length === 0) return null;

    const handleSort = (col: 'label' | number) => {
        if (sortCol === col) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortCol(col);
            setSortDir(col === 'label' ? 'asc' : 'desc');
        }
    };

    const sortedRows = [...rows].sort((a, b) => {
        let aVal: string | number;
        let bVal: string | number;
        if (sortCol === 'label') {
            aVal = getLabelOrdinal(a.label);
            bVal = getLabelOrdinal(b.label);
        } else {
            aVal = a.values.find(v => v.year === sortCol)?.value ?? 0;
            bVal = b.values.find(v => v.year === sortCol)?.value ?? 0;
        }
        if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
        return 0;
    });

    const SortIcon = ({ col }: { col: 'label' | number }) => {
        if (sortCol !== col) return <span style={{ opacity: 0.3, marginLeft: 4 }}>↕</span>;
        return <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
    };

    const hasPercent = (colIndex: number) => colIndex > 0;
    const showSum = rows.length > 1;

    const columnSums: Map<number, number> = new Map();
    if (showSum) {
        columns.forEach(col => {
            let sum = 0;
            rows.forEach(row => {
                const val = row.values.find(v => v.year === col.year);
                if (val) sum += val.value;
            });
            columnSums.set(col.year, sum);
        });
    }

    const sumBorderStyle = '2px solid #A81A40';

    const renderPercent = (pct: number | null) => {
        if (pct === null) return <span style={{ color: '#999', fontSize: '10px' }}>N/A</span>;
        const color = pct >= 0 ? 'color_green' : 'color_red';
        return (
            <span className={color} style={{ fontSize: '10px', fontWeight: 600 }}>
                {pct >= 0 ? '+' : ''}{pct.toFixed(1)}%
            </span>
        );
    };

    return (
        <div className="mgt-40">
            <h3 className="title_h4">{title}</h3>
            {subtitle && <p className="text_s12 color_grey mgt-5">{subtitle}</p>}

            <div className={`report-table-wrap mgt-20${rows.length <= 1 ? ' report-table-wrap--single' : ''}`}>
              <div className="table_scroll">
                <table className="table">
                    <thead>
                        <tr>
                            <th
                                onClick={() => handleSort('label')}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                            >
                                <Trans>Názov</Trans><SortIcon col="label" />
                            </th>
                            {columns.map((col, i) => (
                                <th
                                    key={i}
                                    onClick={() => handleSort(col.year)}
                                    style={{ borderBottom: `3px solid ${getYearColor(i)}`, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                                >
                                    {col.label}<SortIcon col={col.year} />
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sortedRows.map((row, ri) => (
                            <tr key={ri}>
                                <td>
                                    <strong>{row.label}</strong>
                                    {row.date && (
                                        <span className="text_s10 color_grey diblock">{row.date}</span>
                                    )}
                                </td>
                                {columns.map((col, ci) => {
                                    const val = row.values.find(v => v.year === col.year);
                                    const currentValue = val ? val.value : 0;

                                    let yoyPercent: number | null = null;
                                    if (hasPercent(ci)) {
                                        const refCol = columns[0];
                                        const refVal = row.values.find(v => v.year === refCol.year);
                                        const referenceValue = refVal ? refVal.value : 0;
                                        yoyPercent = computeYoYPercent(referenceValue, currentValue);
                                    }

                                    return (
                                        <td key={ci}>
                                            {val && val.value > 0 ? (
                                                <div className="table_drop">
                                                    <span>{numberWithSpaces(val.value)}</span>
                                                    {hasPercent(ci) && (
                                                        <div style={{ marginTop: '2px' }}>
                                                            {renderPercent(yoyPercent)}
                                                        </div>
                                                    )}
                                                    <div className="drop">
                                                        <div><Trans>Názov</Trans>: <strong>{row.label}</strong></div>
                                                        <div><Trans>Dátum</Trans>: <strong>{col.label}</strong></div>
                                                        <div><Trans>Stav</Trans>: <strong className="color_green">{numberWithSpaces(val.value)}</strong></div>
                                                        {yoyPercent !== null && (
                                                            <div>
                                                                <Trans>Zmena</Trans>: <strong className={yoyPercent >= 0 ? 'color_green' : 'color_red'}>
                                                                    {yoyPercent >= 0 ? '+' : ''}{yoyPercent.toFixed(2)}%
                                                                </strong>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <span style={{ color: '#999', fontStyle: 'italic' }}>N/A</span>
                                                    {hasPercent(ci) && (
                                                        <div style={{ marginTop: '2px' }}>
                                                            {renderPercent(yoyPercent)}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                    {showSum && (
                        <tfoot>
                            <tr>
                                <td style={{ borderTop: sumBorderStyle, fontWeight: 700, fontSize: '13px' }}>
                                    <Trans>Celkom</Trans> (Σ)
                                </td>
                                {columns.map((col, ci) => {
                                    const sum = columnSums.get(col.year) ?? 0;
                                    let sumYoY: number | null = null;
                                    if (hasPercent(ci)) {
                                        const refSum = columnSums.get(columns[0].year) ?? 0;
                                        sumYoY = computeYoYPercent(refSum, sum);
                                    }
                                    return (
                                        <td key={ci} style={{ borderTop: sumBorderStyle, textAlign: 'center' }}>
                                            <div style={{ fontWeight: 700, fontSize: '13px' }}>
                                                {sum > 0 ? numberWithSpaces(sum) : (
                                                    <span style={{ color: '#999', fontStyle: 'italic', fontWeight: 400 }}>N/A</span>
                                                )}
                                            </div>
                                            {hasPercent(ci) && (
                                                <div style={{ marginTop: '2px' }}>
                                                    {renderPercent(sumYoY)}
                                                </div>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        </tfoot>
                    )}
                </table>
              </div>
            </div>
        </div>
    );
};

export default ReportComparisonTable;
